/**
 * Bank-rule transport: the two rule sets that decide what a statement line
 * does before a human sees it.
 *
 * A REFERENCE rule pulls a document reference out of free-text narrative so
 * the exact-match pass has something to match on. They are evaluated in
 * `priority` order, which is what makes a line carrying two plausible
 * references resolve the same way every time.
 *
 * A POSTING rule handles the lines that will never match a document because
 * no document was ever raised for them — bank charges, credit interest, FX
 * commission. They run BEFORE the match engine, in `sequence` order, and the
 * first match wins.
 *
 * Both are replaced whole (PUT) rather than patched: a rule is small, and a
 * half-applied rule is worse than a rejected one.
 */
import type { components } from "@erp/api-client";

import { apiFetch, type Page } from "@/lib/api/http";

type Schemas = components["schemas"];

export type BankReferenceRule = Schemas["BankReferenceRule"];
export type BankReferenceRuleWrite = Schemas["BankReferenceRuleWrite"];
export type BankPostingRule = Schemas["BankPostingRule"];
export type BankPostingRuleWrite = Schemas["BankPostingRuleWrite"];
export type BankDocumentType = Schemas["BankDocumentType"];
export type BankRuleDirection = Schemas["BankRuleDirection"];

/** What a reference rule can say a line points at. */
export const DOCUMENT_TYPES: readonly BankDocumentType[] = [
  "customer_receipt",
  "supplier_payment",
  "customer_invoice",
  "supplier_invoice",
  "gateway_payout",
  "posting_rule",
];

export const DIRECTIONS: readonly BankRuleDirection[] = ["inbound", "outbound", "any"];

// ── Reference rules ──────────────────────────────────────────────────────

export function listBankReferenceRules(signal?: AbortSignal): Promise<Page<BankReferenceRule>> {
  return apiFetch<Page<BankReferenceRule>>("/bank-reference-rules", {
    query: { limit: 200 },
    signal,
  });
}

export function createBankReferenceRule(
  body: BankReferenceRuleWrite,
  idempotencyKey: string,
): Promise<BankReferenceRule> {
  return apiFetch<BankReferenceRule>("/bank-reference-rules", {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function replaceBankReferenceRule(
  bankReferenceRuleId: string,
  body: BankReferenceRuleWrite,
): Promise<BankReferenceRule> {
  return apiFetch<BankReferenceRule>(
    `/bank-reference-rules/${encodeURIComponent(bankReferenceRuleId)}`,
    { method: "PUT", body },
  );
}

export function deleteBankReferenceRule(bankReferenceRuleId: string): Promise<void> {
  return apiFetch<void>(`/bank-reference-rules/${encodeURIComponent(bankReferenceRuleId)}`, {
    method: "DELETE",
  });
}

// ── Auto-posting rules ───────────────────────────────────────────────────

export function listBankPostingRules(signal?: AbortSignal): Promise<Page<BankPostingRule>> {
  return apiFetch<Page<BankPostingRule>>("/bank-posting-rules", { query: { limit: 200 }, signal });
}

export function createBankPostingRule(
  body: BankPostingRuleWrite,
  idempotencyKey: string,
): Promise<BankPostingRule> {
  return apiFetch<BankPostingRule>("/bank-posting-rules", {
    method: "POST",
    body,
    idempotencyKey,
  });
}

export function replaceBankPostingRule(
  bankPostingRuleId: string,
  body: BankPostingRuleWrite,
): Promise<BankPostingRule> {
  return apiFetch<BankPostingRule>(`/bank-posting-rules/${encodeURIComponent(bankPostingRuleId)}`, {
    method: "PUT",
    body,
  });
}

export function deleteBankPostingRule(bankPostingRuleId: string): Promise<void> {
  return apiFetch<void>(`/bank-posting-rules/${encodeURIComponent(bankPostingRuleId)}`, {
    method: "DELETE",
  });
}

/**
 * A posting rule must constrain something. One that names no counterparty, no
 * reference and no amount bound would swallow every line on the statement,
 * which is the one failure mode auto-posting must not have — the server
 * answers `bank.validation_failed`, and the form checks it first so the user
 * is told why rather than shown a rejection.
 */
export function postingRuleConstrains(rule: {
  counterparty_pattern?: string;
  reference_pattern?: string;
  min_amount?: string;
  max_amount?: string;
}): boolean {
  return (
    (rule.counterparty_pattern ?? "").trim() !== "" ||
    (rule.reference_pattern ?? "").trim() !== "" ||
    (rule.min_amount ?? "").trim() !== "" ||
    (rule.max_amount ?? "").trim() !== ""
  );
}

/** A pattern that does not compile is skipped by the extractor at three in the
 *  morning next month. Refusing it here is the only place that failure is
 *  visible to whoever wrote it. */
export function patternCompiles(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}
